<template>
  <div class="login-container">
    <div class="login-card">
      <!-- Left Column - Brand Information -->
      <div class="brand-column">
        <div class="brand-content">
          <img src="/logo_routebox_2_blue.svg" alt="RouteBox" class="logo" />
          <h1 class="welcome-title">Welcome to RouteBox</h1>
          <p class="brand-description">
            Optimize your logistics with our cutting-edge platform. Streamline operations, reduce costs, and enhance efficiency.
          </p>
          <p class="brand-tagline">RouteBox - Moving your business forward.</p>
        </div>
      </div>

      <!-- Right Column - Login Form -->
      <div class="form-section">
        <h2 class="form-title">Login to your Account</h2>
        <p class="form-subtitle">Access your dashboard and manage your logistics.</p>

        <div v-if="!isSignIn" class="tab-switch">
          <span>Already have an account?</span>
          <button @click="isSignIn = true" class="text-button">Sign In</button>
        </div>
        <div v-else class="tab-switch">
          <span>Don't have an account?</span>
          <button @click="isSignIn = false" class="text-button">Sign up</button>
        </div>        <form @submit.prevent="handleSubmit" class="auth-form">
          <!-- Display Name Field (Sign Up Only) -->
          <div v-if="!isSignIn" class="form-group">
            <label for="displayName">Full Name</label>
            <input
              id="displayName"
              v-model="form.displayName"
              type="text"
              placeholder="Enter your full name"
              :class="{ error: errors.displayName }"
            />
            <span v-if="errors.displayName" class="error-message">{{ errors.displayName }}</span>
          </div>

          <!-- Email Field -->
          <div class="form-group">
            <label for="email">Email Address</label>
            <div class="input-with-icon">
              <span class="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </span>
              <input
                id="email"
                v-model="form.email"
                type="email"
                placeholder="you@example.com"
                :class="{ error: errors.email }"
              />
            </div>
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password">Password</label>
            <div class="input-with-icon">
              <span class="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </span>
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="••••••••"
                :class="{ error: errors.password }"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
          </div>

          <!-- Confirm Password Field (Sign Up Only) -->
          <div v-if="!isSignIn" class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <div class="input-with-icon">
              <span class="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </span>
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                placeholder="••••••••"
                :class="{ error: errors.confirmPassword }"
              />
            </div>
            <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
          </div>

          <div class="extras">
            <div class="remember-me" v-if="isSignIn">
              <a href="#" class="forgot-link">Forgot password?</a>
            </div>
          </div>          <!-- Error/Success Message -->
          <div v-if="authError" class="message-alert" :class="messageType">
            {{ authError }}
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            class="submit-button"
            :disabled="loading"
          >
            <span v-if="loading" class="loading-spinner"></span>
            {{ loading ? 'Please wait...' : (isSignIn ? 'Login' : 'Create Account') }}
          </button>
        </form>

        <!-- Divider -->
        <div class="divider">
          <span>Or continue with</span>
        </div>

        <!-- Google Sign In -->
        <button 
          @click="handleGoogleSignIn" 
          class="google-button"
          :disabled="loading"
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Sign in with Google</span>
        </button>
        
        <div class="sign-up-prompt">
          <p v-if="isSignIn">Don't have an account? <button @click="isSignIn = false" class="text-button">Sign up</button></p>
          <p v-else>Already have an account? <button @click="isSignIn = true" class="text-button">Sign in</button></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import AuthService from '../services/AuthService'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
      const isSignIn = ref(true)
    const loading = ref(false)
    const showPassword = ref(false)
    const authError = ref('')
    const messageType = ref('error') // 'error' or 'success'

    const form = reactive({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: ''
    })

    const errors = reactive({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: ''
    })

    const validateForm = () => {
      // Reset errors
      Object.keys(errors).forEach(key => errors[key] = '')
      
      let isValid = true

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!form.email) {
        errors.email = 'Email is required'
        isValid = false
      } else if (!emailRegex.test(form.email)) {
        errors.email = 'Please enter a valid email address'
        isValid = false
      }

      // Password validation
      if (!form.password) {
        errors.password = 'Password is required'
        isValid = false
      } else if (form.password.length < 6) {
        errors.password = 'Password must be at least 6 characters'
        isValid = false
      }

      // Sign up specific validations
      if (!isSignIn.value) {
        if (!form.displayName.trim()) {
          errors.displayName = 'Full name is required'
          isValid = false
        }

        if (!form.confirmPassword) {          errors.confirmPassword = 'Please confirm your password'
          isValid = false
        } else if (form.password !== form.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match'
          isValid = false
        }
      }

      return isValid
    }

    const handleSubmit = async () => {
      authError.value = ''
      messageType.value = 'error'
      
      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        if (isSignIn.value) {
          // Sign in
          const result = await AuthService.signInWithEmail(form.email, form.password)
          
          if (result.error) {
            authError.value = result.error
            messageType.value = 'error'
          } else {
            // Check user status (email verification and permissions)
            const userStatus = await AuthService.getUserStatus(result.user.uid)
            
            if (!userStatus.emailVerified) {
              authError.value = 'Please verify your email address before signing in. Check your inbox for the verification email.'
              messageType.value = 'error'
              return
            }
            
            // Check if user has any permissions
            const hasPermissions = Object.values(userStatus.permissions).some(p => p === true)
            if (hasPermissions) {
              // Redirect to originally requested page or first available page
              const availablePages = Object.keys(userStatus.permissions).filter(page => userStatus.permissions[page])
              const redirect = router.currentRoute.value.query.redirect || `/${availablePages[0]}`
              router.push(redirect)
            } else {
              router.push('/pending')
            }
          }
        } else {
          // Sign up
          const result = await AuthService.signUpWithEmail(form.email, form.password, form.displayName)
          
          if (result.error) {
            authError.value = result.error
            messageType.value = 'error'
          } else {
            // Show success message for email verification
            authError.value = result.message || 'Account created successfully! Please check your email to verify your account before signing in.'
            messageType.value = 'success'
            
            // Switch to sign in mode after a delay
            setTimeout(() => {
              isSignIn.value = true
              authError.value = ''
            }, 5000)
            
            // Clear form
            form.password = ''
            form.confirmPassword = ''
          }
        }
      } catch (error) {
        console.error('Authentication error:', error)
        authError.value = 'An unexpected error occurred. Please try again.'
        messageType.value = 'error'
      } finally {
        loading.value = false
      }
    }

    const handleGoogleSignIn = async () => {
      authError.value = ''
      loading.value = true

      try {
        const result = await AuthService.signInWithGoogle()
        
        if (result.error) {
          authError.value = result.error
        } else {
          // Create user in database if it's their first time signing in with Google
          try {
            await AuthService.createUserInDatabase(result.user)
          } catch (error) {
            // User might already exist, that's okay
          }
          
          // Check if user has any permissions
          const hasPermissions = await AuthService.hasAnyPermissions(result.user.uid)
          if (hasPermissions) {
            // Redirect to originally requested page or home
            const redirect = router.currentRoute.value.query.redirect || '/home'
            router.push(redirect)
          } else {
            router.push('/pending')
          }
        }
      } catch (error) {
        console.error('Google sign-in error:', error)
        authError.value = 'An unexpected error occurred. Please try again.'
      } finally {
        loading.value = false
      }
    }

    return {      isSignIn,
      loading,
      showPassword,
      authError,
      messageType,
      form,
      errors,
      handleSubmit,
      handleGoogleSignIn
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f8;
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 1000px;
  display: flex;
  overflow: hidden;
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Brand Column (Left Side) */
.brand-column {
  background: #0077c2;
  color: white;
  padding: 60px 40px;
  width: 45%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.brand-column::before {
  content: '';
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle at top left, rgba(255,255,255,0.1) 0%, transparent 60%);
  z-index: 0;
}

.brand-content {
  position: relative;
  z-index: 1;
}

.logo {
  width: 220px;
  height: 180px;
  margin-bottom: 30px;
  background: white;
  padding: 10px;
  border-radius: 8px;
}

.welcome-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 20px 0;
  font-family: 'Sansation', sans-serif;
  line-height: 1.2;
}

.brand-description {
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 30px;
  opacity: 0.9;
}

.brand-tagline {
  font-size: 14px;
  opacity: 0.7;
  font-style: italic;
  margin-top: auto;
}

/* Form Section (Right Side) */
.form-section {
  padding: 50px;
  width: 55%;
  display: flex;
  flex-direction: column;
}

.form-title {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 10px 0;
}

.form-subtitle {
  color: #718096;
  font-size: 16px;
  margin: 0 0 30px 0;
}

.tab-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #718096;
}

.text-button {
  background: none;
  border: none;
  color: #0077c2;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
}

.text-button:hover {
  text-decoration: underline;
}

.auth-form {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-group input {
  width: 100%;
  padding: 14px 16px 14px 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s ease;
  background: #ffffff;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #0077c2;
  box-shadow: 0 0 0 3px rgba(0, 119, 194, 0.1);
}

.form-group input.error {
  border-color: #e53e3e;
  background: #fff8f8;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s ease;
  display: flex;
}

.password-toggle:hover {
  color: #0077c2;
}

.error-message {
  color: #e53e3e;
  font-size: 13px;
  margin-top: 6px;
  display: block;
}

.extras {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.forgot-link {
  color: #0077c2;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s;
}

.forgot-link:hover {
  color: #005fa3;
  text-decoration: underline;
}

.message-alert {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border: 1px solid;
  transition: all 0.3s ease;
}

.message-alert.error {
  background: #fed7d7;
  color: #c53030;
  border-color: #feb2b2;
}

.message-alert.success {
  background: #c6f6d5;
  color: #2d7b3e;
  border-color: #9ae6b4;
}

.submit-button {
  width: 100%;
  padding: 14px;
  background: #0077c2;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}

.submit-button:hover:not(:disabled) {
  background: #005fa3;
  transform: translateY(-1px);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.divider {
  text-align: center;
  margin: 24px 0;
  position: relative;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e2e8f0;
}

.divider span {
  background: white;
  color: #718096;
  padding: 0 16px;
  font-size: 14px;
  position: relative;
  z-index: 1;
}

.google-button {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #2d3748;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.google-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #cbd5e0;
}

.google-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.sign-up-prompt {
  margin-top: 20px;
  text-align: center;
  color: #718096;
  font-size: 14px;
}

/* Responsive design */
@media (max-width: 900px) {
  .login-card {
    flex-direction: column;
    max-width: 500px;
  }
  
  .brand-column {
    width: 100%;
    padding: 30px;
  }
  
  .form-section {
    width: 100%;
    padding: 30px;
  }
  
  .welcome-title {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .login-card {
    border-radius: 0;
    box-shadow: none;
    height: 100vh;
  }
  
  .brand-column {
    padding: 20px;
  }
  
  .form-section {
    padding: 20px;
  }
  
  .form-group input,
  .submit-button,
  .google-button {
    padding: 12px;
  }
  
  .form-title {
    font-size: 20px;
  }
}
</style>
